import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { db } from "../firebase";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { FOOD_DB, applyFilters, getRandomReassurance } from "../utils/suggestions";
import { globalStyles, resultStyles } from "../styles";
import { useSubscription } from "../context/SubscriptionContext";

const lockStyles = {
  card: {
    maxWidth: 400,
    margin: "80px auto",
    padding: "36px 28px",
    background: "rgba(255,255,255,0.92)",
    borderRadius: 24,
    boxShadow: "0 20px 48px rgba(15,23,42,0.10)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    fontFamily: "'Inter', Arial, sans-serif",
    textAlign: "center",
  },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" },
  subtitle: { margin: 0, fontSize: 14, color: "#64748b", lineHeight: 1.6 },
  primary: {
    padding: "12px 16px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  secondary: {
    padding: "10px 16px",
    borderRadius: 14,
    border: "1px solid #fed7cc",
    background: "transparent",
    color: "#8a3c2f",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
};

const recipeStyles = {
  card: {
    background: "rgba(255,255,255,0.95)",
    borderRadius: 20,
    padding: "24px 20px",
    boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
    marginBottom: 16,
    fontFamily: "'Inter', Arial, sans-serif",
  },
  title: { margin: "0 0 12px", fontSize: 20, fontWeight: 700, color: "#0f172a" },
  loading: { color: "#94a3b8", fontSize: 14 },
  image: {
    width: "100%",
    borderRadius: 14,
    marginBottom: 14,
    objectFit: "cover",
    maxHeight: 220,
  },
  meta: {
    display: "flex",
    gap: 16,
    fontSize: 13,
    color: "#64748b",
    marginBottom: 12,
    fontWeight: 500,
  },
  sectionLabel: {
    margin: "12px 0 6px",
    fontSize: 13,
    fontWeight: 700,
    color: "#f25f4c",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  list: { margin: 0, paddingLeft: 20, fontSize: 14, color: "#334155", lineHeight: 1.8 },
  upgradeCard: {
    background: "linear-gradient(135deg, #fff5f3, #fff)",
    border: "1.5px solid #fed7cc",
    borderRadius: 20,
    padding: "20px 18px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 16,
    fontFamily: "'Inter', Arial, sans-serif",
  },
  upgradeTitle: { margin: "0 0 2px", fontWeight: 700, fontSize: 15, color: "#0f172a" },
  upgradeSubtitle: { margin: 0, fontSize: 13, color: "#64748b" },
  upgradeBtn: {
    marginLeft: "auto",
    whiteSpace: "nowrap",
    padding: "10px 16px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
  },
};

const twoColGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 16,
  marginTop: 18,
  alignItems: "start",
};

const saveNudgeStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  padding: "12px 18px",
  borderRadius: 14,
  background: "rgba(255,255,255,0.7)",
  border: "1px solid #e2e8f0",
  fontSize: 14,
  color: "#475569",
  marginBottom: 16,
  fontFamily: "'Inter', sans-serif",
};

const saveNudgeBtn = {
  padding: "8px 16px",
  borderRadius: 999,
  border: "none",
  background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const faqItems = [
  {
    question: "1. What does this app do?",
    answer:
      "NextBite helps you decide what to offer your child next based on what they just ate. It gives simple, practical suggestions to reduce stress around meals."
  },
  {
    question: "2. What if my child refuses the suggested food?",
    answer:
      "That’s completely normal. The app is meant to guide, not guarantee outcomes. Even small exposures help over time."
  },
  {
    question: "3. Can I enter foods not listed?",
    answer:
      "Yes! You can type any food in the input box, and the app will treat it just like a recommended option."
  },
  {
    question: "4. How are suggestions generated?",
    answer:
      "Suggestions are based on simple food progression logic—moving from what your child accepted toward slightly more balanced or nutritious options."
  },
  {
    question: "5. Does this replace professional advice?",
    answer:
      "No. This app is meant to support everyday decisions, not replace guidance from pediatricians or nutrition experts."
  },
  {
    question: "6. Is my data saved or shared?",
    answer:
      "Your data is used only to improve your experience. We do not sell or share your data."
  },
  {
    question: "7. What if my child has allergies?",
    answer:
      "Please use your judgment and avoid any foods that may trigger allergies. You can also enter safe foods manually."
  },
  {
    question: "8. Why are suggestions sometimes repetitive?",
    answer:
      "The app focuses on consistency and gradual exposure. Repetition is intentional and helps children become more comfortable with foods."
  }
];

export default function Result() {
  const location = useLocation();
  const nav = useNavigate();
  const browserTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const { isPremium, isRegistered, isGuestLocked, incrementGuestUsage } = useSubscription();

  const [historyData, setHistoryData] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [guestLocked, setGuestLocked] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [recipeLoading, setRecipeLoading] = useState(false);

  const state = useMemo(() => {
    if (location.state) {
      try {
        localStorage.setItem("nextbiteResultState", JSON.stringify(location.state));
      } catch {
        // ignore storage errors
      }
      return location.state;
    }

    try {
      return JSON.parse(localStorage.getItem("nextbiteResultState") || "null");
    } catch {
      return null;
    }
  }, [location.state]);

  useEffect(() => {
    async function loadHistory() {
      const snapshot = await getDocs(collection(db, "history"));
      const data = snapshot.docs.map((doc) => doc.data());
      setHistoryData(data);
    }

    loadHistory();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // Guest time-lock: increment on each visit, block if over limit
  useEffect(() => {
    if (!isRegistered) {
      if (isGuestLocked()) {
        setGuestLocked(true);
      } else {
        incrementGuestUsage();
      }
    }
  }, [isRegistered, isGuestLocked, incrementGuestUsage]);

  // Fetch recipe+image for selected dish (premium only)
  useEffect(() => {
    if (!selectedDish || !isPremium) return;
    let cancelled = false;
    setRecipe(null);
    setRecipeLoading(true);
    fetch("/api/recipe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dishName: selectedDish }),
    })
      .then((r) => r.json())
      .then((data) => { if (!cancelled) setRecipe(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRecipeLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDish, isPremium]);

  const cuisines = useMemo(() => {
    if (Array.isArray(state?.cuisines)) {
      return state.cuisines;
    }

    return state?.cuisine ? [state.cuisine] : [];
  }, [state?.cuisine, state?.cuisines]);
  const cuisine = cuisines.join(", ");
  const selectedLocation = state?.location || "";
  const texture = state?.texture || "";
  const preferences = state?.preferences || "";
  const allergies = useMemo(() => state?.allergies || [], [state?.allergies]);

  const fallbackSuggestions = useMemo(() => {
    const filtered = applyFilters(FOOD_DB, preferences, allergies, cuisines);

    const scored = filtered.map((food) => {
      let score = 0;

      const frequency = historyData.filter((h) =>
        h.suggestions?.includes(food.name)
      ).length;
      score += frequency * 2;

      if (cuisines.some((item) => food.tags.includes(item.toLowerCase()))) {
        score += 10;
      }

      if (texture === "Soft" && food.tags.includes("soft")) {
        score += 5;
      }

      score += Math.random() * 2;

      const recentSuggestions = historyData
        .slice(-5)
        .flatMap((h) => h.suggestions || []);
      if (!recentSuggestions.includes(food.name)) {
        score += 3;
      }

      return { ...food, score };
    });

    scored.sort((a, b) => b.score - a.score);

    return {
      next: scored.slice(0, 3),
      upgrade: scored.slice(3, 8)
    };
  }, [cuisines, texture, preferences, allergies, historyData]);

  useEffect(() => {
    if (!state) return;

    const controller = new AbortController();

    async function loadAiSuggestions() {
      setSuggestionsLoading(true);

      try {
        const response = await fetch("/api/suggestions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            cuisines,
            location: selectedLocation,
            concerns: state.concerns || "",
            texture,
            preferences,
            allergies
          }),
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error("AI suggestions unavailable.");
        }

        const data = await response.json();
        setAiSuggestions({
          next: Array.isArray(data.next) ? data.next : [],
          upgrade: Array.isArray(data.upgrade) ? data.upgrade : []
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          setAiSuggestions(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setSuggestionsLoading(false);
        }
      }
    }

    loadAiSuggestions();

    return () => controller.abort();
  }, [state, cuisines, selectedLocation, texture, preferences, allergies]);

  const suggestions = aiSuggestions || fallbackSuggestions;

  useEffect(() => {
    async function saveToHistory() {
      if (!state) return;
      try {
        await addDoc(collection(db, "history"), {
          cuisine,
          cuisines,
          location: selectedLocation,
          texture,
          preferences,
          allergies,
          suggestions: suggestions.next.map((i) => i.name),
          timestamp: new Date(),
          timeZone: browserTimeZone
        });
      } catch (err) {
        console.error("Error saving history:", err);
      }
    }

    if (!suggestionsLoading && suggestions?.next?.length) {
      saveToHistory();
    }
  }, [
    suggestions,
    state,
    cuisine,
    cuisines,
    selectedLocation,
    texture,
    preferences,
    allergies,
    browserTimeZone,
    suggestionsLoading
  ]);

  if (!state) {
    return (
      <div style={globalStyles.page}>
        <p>No data found. Please start again.</p>
        <button onClick={() => nav("/home")} style={globalStyles.primaryBtn}>
          Go Home
        </button>
      </div>
    );
  }

  if (guestLocked) {
    return (
      <div style={globalStyles.page}>
        <div style={lockStyles.card}>
          <div style={{ fontSize: 48, textAlign: "center" }}>🔒</div>
          <h2 style={lockStyles.title}>Daily limit reached</h2>
          <p style={lockStyles.subtitle}>
            Guests can use NextBite 3 times per day. Sign up free to get unlimited suggestions.
          </p>
          <button style={lockStyles.primary} onClick={() => nav("/register")}>
            Create free account
          </button>
          <button style={lockStyles.secondary} onClick={() => nav("/login")}>
            Login
          </button>
        </div>
      </div>
    );
  }

  const message = getRandomReassurance();

  return (
    <div style={globalStyles.page}>
      <div style={resultStyles.page}>
        <div style={resultStyles.header}>
          <h2 style={resultStyles.pageTitle}>Suggestions for you</h2>
        </div>

        {/* Save nudge for guests */}
        {!isRegistered && (
          <div style={saveNudgeStyle}>
            <span>💾 Sign up free to save these results and access them anytime.</span>
            <button onClick={() => nav("/register")} style={saveNudgeBtn}>
              Save results
            </button>
          </div>
        )}

        <div style={twoColGrid}>

          {/* Try this next */}
          <div style={{ ...resultStyles.listCard, ...resultStyles.featuredCard, marginTop: 0 }}>
            <div style={resultStyles.sectionHeader}>
              <span style={resultStyles.sectionBadge}>Try this next</span>
              <p style={resultStyles.sectionDescription}>
                {suggestionsLoading
                  ? "Finding cuisine-aware ideas for your family."
                  : "Strongest suggestions based on your input."}
              </p>
            </div>
            {suggestions.next.map((item) => (
              <div
                key={item.name}
                onClick={() => isPremium && setSelectedDish(item.name)}
                style={{
                  ...resultStyles.listItem,
                  ...resultStyles.primaryListItem,
                  cursor: isPremium ? "pointer" : "default",
                  outline: selectedDish === item.name ? "2px solid #f25f4c" : "none",
                }}
              >
                <span style={resultStyles.listItemTitle}>{item.name}</span>
                <span style={resultStyles.listItemTag}>
                  {isPremium ? "Tap for recipe" : "Best next step"}
                </span>
              </div>
            ))}
          </div>

          {/* Better options */}
          <div style={{ ...resultStyles.listCard, marginTop: 0 }}>
            <div style={resultStyles.sectionHeader}>
              <span style={{ ...resultStyles.sectionBadge, ...resultStyles.secondaryBadge }}>
                Better options
              </span>
              <p style={resultStyles.sectionDescription}>
                Gentle follow-ups once the next bite feels comfortable.
              </p>
            </div>
            {suggestions.upgrade.map((item) => (
              <div key={item.name} style={resultStyles.listItem}>
                <span style={resultStyles.listItemTitle}>{item.name}</span>
                <span style={resultStyles.listItemTagMuted}>Keep in rotation</span>
              </div>
            ))}
          </div>

        </div>

        {/* Premium recipe panel */}
        {isPremium && selectedDish && (
          <div style={recipeStyles.card}>
            <h3 style={recipeStyles.title}>{selectedDish}</h3>
            {recipeLoading && <p style={recipeStyles.loading}>Fetching recipe...</p>}
            {!recipeLoading && recipe && (
              <>
                {recipe.imageUrl && (
                  <img
                    src={recipe.imageUrl}
                    alt={selectedDish}
                    style={recipeStyles.image}
                  />
                )}
                <div style={recipeStyles.meta}>
                  {recipe.recipe?.time && <span>⏱ {recipe.recipe.time}</span>}
                  {recipe.recipe?.serves && <span>👶 {recipe.recipe.serves}</span>}
                </div>
                {recipe.recipe?.ingredients && (
                  <>
                    <p style={recipeStyles.sectionLabel}>Ingredients</p>
                    <ul style={recipeStyles.list}>
                      {recipe.recipe.ingredients.map((ing, i) => (
                        <li key={i}>{ing}</li>
                      ))}
                    </ul>
                  </>
                )}
                {recipe.recipe?.steps && (
                  <>
                    <p style={recipeStyles.sectionLabel}>Steps</p>
                    <ol style={recipeStyles.list}>
                      {recipe.recipe.steps.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Upgrade prompt for registered non-premium users */}
        {!isPremium && isRegistered && (
          <div style={recipeStyles.upgradeCard}>
            <span style={{ fontSize: 28 }}>📖</span>
            <div>
              <p style={recipeStyles.upgradeTitle}>Get recipes + photos</p>
              <p style={recipeStyles.upgradeSubtitle}>
                Tap any dish to see a full recipe and photo. 7-day free trial.
              </p>
            </div>
            <button style={recipeStyles.upgradeBtn} onClick={() => nav("/upgrade")}>
              Try Premium
            </button>
          </div>
        )}

        {/* Upgrade prompt for guests */}
        {!isPremium && !isRegistered && (
          <div style={recipeStyles.upgradeCard}>
            <span style={{ fontSize: 28 }}>✨</span>
            <div>
              <p style={recipeStyles.upgradeTitle}>Want recipes too?</p>
              <p style={recipeStyles.upgradeSubtitle}>
                Sign up free, then unlock Premium for $3/month.
              </p>
            </div>
            <button style={recipeStyles.upgradeBtn} onClick={() => nav("/register")}>
              Sign up free
            </button>
          </div>
        )}

        <div style={resultStyles.messageCard}>
          <p style={resultStyles.message}>{message}</p>
        </div>

        <div style={resultStyles.selectionCard}>
          <h3 style={resultStyles.selectionTitle}>Your current selections</h3>

          <div style={resultStyles.selectionGrid}>
            <div style={resultStyles.selectionItem}>
              <p style={resultStyles.subtle}>Cuisine</p>
              <strong>{cuisine || "Any"}</strong>
            </div>
            <div style={resultStyles.selectionItem}>
              <p style={resultStyles.subtle}>Texture</p>
              <strong>{texture || "Any"}</strong>
            </div>
            <div style={resultStyles.selectionItem}>
              <p style={resultStyles.subtle}>Location</p>
              <strong>{selectedLocation || "Not set"}</strong>
            </div>
            <div style={resultStyles.selectionItem}>
              <p style={resultStyles.subtle}>Diet</p>
              <strong>{preferences || "None"}</strong>
            </div>
          </div>

          <div style={resultStyles.selectionPillRow}>
            <span style={resultStyles.selectionPillLabel}>Allergies</span>
            {allergies?.length ? (
              allergies.map((allergy) => (
                <span key={allergy} style={resultStyles.selectionPill}>
                  {allergy}
                </span>
              ))
            ) : (
              <span style={resultStyles.selectionPillMuted}>None listed</span>
            )}
          </div>
        </div>

        <div style={resultStyles.faqCard}>
          <h3 style={resultStyles.faqTitle}>FAQ</h3>

          {faqItems.map((item) => (
            <div key={item.question} style={resultStyles.faqItem}>
              <h4 style={resultStyles.faqQuestion}>{item.question}</h4>
              <p style={resultStyles.faqAnswer}>{item.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
